import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Headline from '@components/atoms/headline/headline';
import { ArrowLeft, ArrowRight } from '@components/icons/arrow/arrow';
import Container from '@components/layout/container/container';
import Link from 'next/link';
import StyledEducationTabletProgress from './educationTabletProgress.style';

function EducationTabletProgress({
  stepsWithLastStep,
  activeIndex,
  prevIndex,
  nextIndex,
  educationTopics,
  topic,
  shapeBurst,
  uri
}) {
  const router = useRouter();
  const stepVideo = useRef();
  const [nextStepIndex, setNextStepIndex] = useState();
  const multipleTopics = Array.isArray(educationTopics) && educationTopics.length > 1;

  const triggerStepVideo = index => {
    setNextStepIndex(index);
    stepVideo.current.play();
  };

  const isMobile = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 1280;
    }
    return false;
  };

  const scrollToTop = () => {
    const delayDuration = 600;
    const scrollDuration = 5;
    const firefoxScrollYOffset = 4;

    const scrollStep = () => {
      const targetPosition = activeIndex >= stepsWithLastStep.length ? 0 : window.scrollY;
      const scrollDistance = targetPosition <= firefoxScrollYOffset ? 0 : targetPosition;

      if (scrollDistance !== 0) {
        window.scrollBy(0, -scrollDistance / scrollDuration);
        window.requestAnimationFrame(scrollStep);
      } else if (scrollDistance === 0) {
        window.scrollTo(0, 0);
      }
    };

    setTimeout(() => {
      if (window.scrollY !== 0 && activeIndex >= stepsWithLastStep.length) {
        scrollStep();
      } else {
        scrollStep();
      }
    }, delayDuration);
  };

  return (
    <StyledEducationTabletProgress category={topic} multipleTopics={multipleTopics}>
      <Container>
        <div className="progress__navigation" id="bottom">
          <div>
            {activeIndex !== 1 && (
              <div className="progress__navigation-link link--back">
                <div className="progress__navigation-arrow">
                  <ArrowLeft />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setNextStepIndex(prevIndex);
                    router.push(`${uri}/steps?step=${prevIndex}#bottom`, undefined, {
                      shallow: true
                    });
                    scrollToTop();
                  }}
                  className="progress__navigation-button"
                >
                  Zurück
                </button>
              </div>
            )}
          </div>
          {activeIndex < stepsWithLastStep.length && (
            <div className="progress__navigation-link link--next">
              <button
                type="button"
                onClick={() => triggerStepVideo(nextIndex)}
                className="progress__navigation-button"
              >
                Weiter
              </button>

              <div className="progress__navigation-arrow">
                <ArrowRight />
              </div>
            </div>
          )}
          {activeIndex >= stepsWithLastStep.length && (
            <div className="progress__navigation-link link--close">
              <Link href={`${uri}`} className="progress__navigation-button">
                Beenden
              </Link>
              <div className="progress__navigation-arrow">
                <ArrowRight />
              </div>
            </div>
          )}
        </div>
      </Container>
      <div className="progress__bar">
        <div className="progress__bar-background" />
        <div className="progress__bar-steps">
          {stepsWithLastStep.map(step => {
            const isLast = step.number === stepsWithLastStep.length;
            let stepClass = 'step--open';

            if (step.number === activeIndex) {
              stepClass = 'step--current';
            } else if (step.number < activeIndex) {
              stepClass = 'step--completed';
            }

            if (
              (isMobile() &&
                (step.number === activeIndex ||
                  step.number === prevIndex ||
                  step.number === nextIndex)) ||
              !isMobile()
            ) {
              return (
                <React.Fragment key={step.number}>
                  <div className={`progress__bar-line ${stepClass}`} />
                  <div className="progress__bar-step-container">
                    {step.number === activeIndex && shapeBurst !== '' && (
                      <video
                        ref={stepVideo}
                        className="progress__bar-step-video"
                        controls={false}
                        playsInline
                        muted
                        onPlay={e => {
                          e.target.style.display = 'block';
                        }}
                        onEnded={e => {
                          e.target.style.display = 'none';
                          router.push(`${uri}/steps?step=${nextStepIndex}#bottom`, undefined, {
                            shallow: true
                          });
                          scrollToTop();
                        }}
                      >
                        <source src={shapeBurst} type="video/mp4" />
                      </video>
                    )}
                    <div
                      className={`progress__bar-step ${stepClass} ${isLast ? 'last-step' : ''} ${
                        stepsWithLastStep.length > 10 ? 'step--small' : ''
                      }`}
                    >
                      {isLast ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 30 30"
                        >
                          <g fill="currentColor">
                            <circle cx="22.44" cy="23.87" r="4.23" />
                            <path d="m18.72 12.44-8.12 2.43 6.43 5.87 1.69-8.3ZM25.34 8.9l-4.43 2.93 1.7 4.74h5.47l1.7-4.74-4.44-2.92ZM11.13 20.1l2.48 3.42-1.73 3.86-4.2.43-2.49-3.43 1.73-3.86 4.21-.43ZM17.71 1l-6.28 2.78 2.79 6.28 6.28-2.78zM.81 13.25l1.22.86.07 1.5 1.5-.02.93 1.16 1.2-.9 1.45.4.44-1.43 1.4-.52-.48-1.42.82-1.25-1.22-.87-.07-1.49-1.5.02-.93-1.17-1.2.9-1.45-.4-.44 1.43-1.4.53.48 1.42-.82 1.25Z" />
                          </g>
                          <defs>
                            <path fill="currentColor" d="M0 0h30v30H0z" />
                          </defs>
                        </svg>
                      ) : (
                        <Headline tag="span">{step.number}</Headline>
                      )}
                    </div>
                    {/* </Link> */}
                  </div>
                </React.Fragment>
              );
            }

            return null;
          })}
          {isMobile() && (
            <div
              className={`progress__bar-line ${
                activeIndex >= stepsWithLastStep.length - 1 ? 'step--hidden' : ''
              }`}
            />
          )}
        </div>
      </div>
    </StyledEducationTabletProgress>
  );
}

EducationTabletProgress.propTypes = {
  stepsWithLastStep: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.number.isRequired
    })
  ).isRequired,
  activeIndex: PropTypes.number.isRequired,
  prevIndex: PropTypes.number.isRequired,
  nextIndex: PropTypes.number.isRequired,
  educationTopics: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired
    })
  ).isRequired,
  topic: PropTypes.string.isRequired,
  shapeBurst: PropTypes.string.isRequired,
  uri: PropTypes.string.isRequired
};

export default EducationTabletProgress;
